import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Flex, Heading, Text, TextField, Box } from "@radix-ui/themes";
import { EnvelopeClosedIcon, CheckIcon } from "@radix-ui/react-icons";
import { logPasswordEvent } from "../../lib/logPasswordEvent";
import { LanguageSelector } from "./LanguageSelector";
import styles from "./Login.module.css";

export function ResetPassword() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const redirectUrl = `${window.location.origin}/update-password`;

    // Edge function handles: user lookup, recovery link generation, email send, and logging
    await logPasswordEvent("password_reset_requested", {
      email,
      redirectTo: redirectUrl,
      lang: i18n.language,
    });

    setSubmitted(true);
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className={styles.container}>
        <Box className={styles.form}>
          <Flex direction="column" gap="4" align="center">
            <Box
              style={{
                backgroundColor: "var(--green-3)",
                borderRadius: "50%",
                padding: "1rem",
              }}
            >
              <CheckIcon width={32} height={32} color="var(--green-11)" />
            </Box>
            <Heading size="6">{t("resetPassword.checkEmail")}</Heading>
            <Text color="gray" size="2" align="center">
              {t("resetPassword.checkEmailDescription")}
            </Text>
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Button variant="soft" size="2" mt="2">
                {t("resetPassword.backToLogin")}
              </Button>
            </Link>
            <Flex justify="center" mt="2">
              <LanguageSelector />
            </Flex>
          </Flex>
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Flex direction="column" gap="4">
          <Box mb="2">
            <Heading size="6" mb="1">
              {t("resetPassword.title")}
            </Heading>
            <Text color="gray" size="2">
              {t("resetPassword.description")}
            </Text>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("login.email")}
            </Text>
            <TextField.Root
              type="email"
              placeholder={t("login.emailPlaceholder")}
              size="3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            >
              <TextField.Slot>
                <EnvelopeClosedIcon />
              </TextField.Slot>
            </TextField.Root>
          </Box>

          <Button type="submit" size="3" mt="2" disabled={loading}>
            {loading ? t("resetPassword.sending") : t("resetPassword.sendLink")}
          </Button>

          <Text size="2" align="center" color="gray">
            {t("resetPassword.rememberPassword")}{" "}
            <Link to="/login" style={{ color: "var(--accent-11)" }}>
              {t("resetPassword.signIn")}
            </Link>
          </Text>

          <Flex justify="center" mt="2">
            <LanguageSelector />
          </Flex>
        </Flex>
      </form>
    </div>
  );
}
